import { createTRPCClient, httpBatchLink, httpLink, splitLink } from '@trpc/client';
import superjson from 'superjson';
import type { AppRouter } from './router-type';

export interface ApiClientOptions {
  /** tRPC endpoint. Dev server proxies `/api` → http://localhost:4000. */
  readonly url?: string;
  /** Injected for tests (see `src/test/fake-api.tsx`). */
  readonly fetch?: typeof fetch;
  /** Batch requests (default true). Tests disable batching to keep the fake simple. */
  readonly batch?: boolean;
}

const CSRF_HEADERS = { 'x-keel-csrf': '1' } as const;
const UNBATCHED: ReadonlySet<string> = new Set(['auth.login', 'auth.register', 'auth.claimGuest', 'auth.requestPasswordReset', 'auth.resetPassword']);

/** Creates the tRPC client: superjson transformer, CSRF header, cookie credentials. */
export const createApiClient = (opts: ApiClientOptions = {}) => {
  const url = opts.url ?? '/api/trpc';
  const baseFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const linkOpts = {
    url,
    transformer: superjson,
    headers: () => CSRF_HEADERS,
    fetch: (input: RequestInfo | URL, init?: RequestInit) => baseFetch(input, { ...init, credentials: 'include' }),
  };
  // sign-in / sign-up calls are rate-limited per request, so the API refuses them inside a batch: send them alone
  return createTRPCClient<AppRouter>({
    links: [opts.batch === false ? httpLink(linkOpts) : splitLink({
      condition: (op) => UNBATCHED.has(op.path), true: httpLink(linkOpts), false: httpBatchLink(linkOpts),
    })],
  });
};

export type ApiClient = ReturnType<typeof createApiClient>;

export interface UploadResult {
  readonly fileToken: string;
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly sha256: string;
}

/** `POST /api/upload` (multipart, field `file`). Returns a short-lived file token for `imports.*` / `attach`. */
export const uploadFile = async (file: File, endpoint = '/api/upload'): Promise<UploadResult> => {
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(endpoint, { method: 'POST', body, credentials: 'include', headers: CSRF_HEADERS });
  if (!res.ok) throw new Error(res.status === 413 ? 'That file is too large to upload.' : `Upload failed (${res.status}).`);
  return (await res.json()) as UploadResult;
};

/** POSTs a backup file to the restore endpoint. */
export const restoreBackup = async (file: File): Promise<void> => {
  const body = new FormData();
  body.append('file', file);
  const res = await fetch('/api/backup/restore', { method: 'POST', body, credentials: 'include', headers: CSRF_HEADERS });
  if (!res.ok) throw new Error(`Restore failed (${res.status}).`);
};
